/**
 * main.tsx — React app entry point
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthProvider } from '@/lib/auth';
import { ToastProvider } from '@/lib/toast';
import App from '@/App';

// Initialise i18n BEFORE React renders
import '@/i18n';
import '@/index.css';

// Local dev must not be served by an old PWA shell; dynamic profile work is
// impossible to verify if service worker caches keep returning stale bundles.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (isLocalHost) {
    const DEV_SW_RESET_FLAG = 'boox_web_dev_sw_reset_done';
    void Promise.allSettled([
      navigator.serviceWorker?.getRegistrations?.().then((registrations) => {
        registrations.forEach((registration) => {
          void registration.unregister();
        });
        return registrations.length;
      }) ?? Promise.resolve(0),
      caches?.keys?.().then((keys) => Promise.all(keys.map((key) => caches.delete(key)))).then((deleted) => deleted.length) ?? Promise.resolve(0),
    ]).then((results) => {
      const registrationCount = results[0].status === 'fulfilled' ? results[0].value : 0;
      const hadController = Boolean(navigator.serviceWorker?.controller);
      const alreadyReloaded = sessionStorage.getItem(DEV_SW_RESET_FLAG) === '1';

      if ((hadController || registrationCount > 0) && !alreadyReloaded) {
        sessionStorage.setItem(DEV_SW_RESET_FLAG, '1');
        window.location.reload();
        return;
      }

      if (!hadController && registrationCount === 0) {
        sessionStorage.removeItem(DEV_SW_RESET_FLAG);
      }
    }).catch(() => {});
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AuthProvider>
      <ToastProvider>
        <App />
      </ToastProvider>
    </AuthProvider>
  </StrictMode>,
);
